/**
 * Tipos derivados de UI: formas de datos que usan los formularios y
 * listados, distintas (a propósito) de las filas crudas de la base de
 * datos en database.types.ts.
 */

import type {
  CitaRow,
  ClienteRow,
  ComisionRow,
  CompraRow,
  CuentaPorCobrarRow,
  CuentaPorPagarRow,
  EstadoCita,
  GastoRow,
  InventarioMovimientoRow,
  PagoClienteRow,
  PagoEmpleadoRow,
  ProductoRow,
  ServicioRow,
  TipoMovimientoInventario,
  VentaDetalleRow,
} from './database.types';

/** Cliente con los datos ya combinados para mostrarlo en tablas. */
export interface ClientaListItem extends ClienteRow {
  nombreCompleto: string;
}

/** Cita con las relaciones resueltas (nombre de clienta, servicio, profesional). */
export interface CitaConRelaciones extends CitaRow {
  cliente_nombre: string;
  servicio_nombre: string;
  profesional_nombre: string;
}

export interface ServicioListItem extends ServicioRow {
  categoria_nombre: string;
}

export const ESTADOS_CITA: { value: EstadoCita; label: string; colorClass: string }[] = [
  { value: 'pendiente', label: 'Pendiente', colorClass: 'bg-amber-100 text-amber-800' },
  { value: 'confirmada', label: 'Confirmada', colorClass: 'bg-blue-100 text-blue-800' },
  { value: 'atendida', label: 'Atendida', colorClass: 'bg-green-100 text-green-800' },
  { value: 'cancelada', label: 'Cancelada', colorClass: 'bg-red-100 text-red-800' },
  { value: 'no_asistio', label: 'No asistió', colorClass: 'bg-gray-200 text-gray-700' },
];

export function estadoCitaBadge(estado: EstadoCita) {
  return ESTADOS_CITA.find((e) => e.value === estado) ?? ESTADOS_CITA[0];
}

export interface ProductoListItem extends ProductoRow {
  categoria_nombre: string;
}

/**
 * Forma reducida que devuelve el RPC `productos_disponibles_venta()` --
 * lo que puede ver una profesional: nombre y precio de venta, nada de
 * costo ni de conteo exacto de inventario. Es un subconjunto estructural
 * de `ProductoRow`, así que cualquier componente que solo lea
 * id/nombre/precio_venta acepta indistintamente esto o el catálogo
 * completo (staff).
 */
export interface ProductoVentaItem {
  id: string;
  codigo: string;
  nombre: string;
  precio_venta: number;
  categoria_id: string | null;
}

export interface MovimientoConProducto extends InventarioMovimientoRow {
  producto_nombre: string;
}

export const TIPOS_MOVIMIENTO_INVENTARIO: { value: TipoMovimientoInventario; label: string; entrada: boolean }[] = [
  { value: 'entrada_compra', label: 'Entrada por compra', entrada: true },
  { value: 'ajuste_positivo', label: 'Ajuste positivo', entrada: true },
  { value: 'salida_venta', label: 'Salida por venta', entrada: false },
  { value: 'ajuste_negativo', label: 'Ajuste negativo', entrada: false },
  { value: 'producto_danado', label: 'Producto dañado', entrada: false },
  { value: 'producto_vencido', label: 'Producto vencido', entrada: false },
  { value: 'consumo_interno', label: 'Consumo interno', entrada: false },
];

/** Tipos de movimiento que un usuario puede registrar manualmente desde
 * el formulario de Inventario. `salida_venta` queda excluido a propósito
 * — ese movimiento solo lo genera el sistema, vía registrar_venta, para
 * que siempre quede ligado a una venta real (ver schema_ventas_inventario.sql). */
export const TIPOS_MOVIMIENTO_MANUAL = TIPOS_MOVIMIENTO_INVENTARIO.filter((t) => t.value !== 'salida_venta');

export interface VentaListItem {
  id: string;
  fecha: string;
  total: number;
  estado: 'confirmada' | 'anulada';
  cliente_nombre: string;
}

/** Una línea de venta en construcción en el formulario — antes de
 * enviarla al RPC `registrar_venta` (ver database.types.ts). */
export interface LineaVentaBorrador {
  key: string; // id local, solo para el render de la lista (React key)
  tipo: 'servicio' | 'producto';
  itemId: string;
  nombre: string;
  cantidad: number;
  precioUnitario: number;
  profesionalId: string | null;
}

export interface PagoVentaBorrador {
  key: string;
  formaPagoId: string;
  monto: number;
}

export interface GastoConCategoria extends GastoRow {
  categoria_nombre: string;
}

/** Compra en la lista, con el nombre del proveedor y lo necesario para
 * saber si quedó bien registrada como contado/crédito -- ver
 * ComprasList.tsx y supabase/schema_compras_corregir_credito.sql.
 * `registrar_compra()` no guarda "es_credito" como columna en `compras`
 * (decide crédito vs. contado con un parámetro al momento de crearla),
 * así que la forma de pago real se deriva de si existe o no una
 * `cuenta_por_pagar` para esa compra. */
export interface CompraListItem extends CompraRow {
  proveedor_nombre: string;
  tiene_cuenta_por_pagar: boolean;
  tiene_egreso_caja: boolean;
  tiene_compensacion_caja: boolean;
}

/** Una línea de compra en construcción en el formulario — antes de
 * enviarla al RPC `registrar_compra` (ver database.types.ts). */
export interface LineaCompraBorrador {
  key: string;
  productoId: string;
  nombre: string;
  cantidad: number;
  costoUnitario: number;
}

export interface CuentaPorPagarConProveedor extends CuentaPorPagarRow {
  proveedor_nombre: string;
}

/** Línea de una venta (`venta_detalle`) con el nombre de servicio o producto
 * ya resuelto, para mostrar qué se vendió dentro del detalle de una cuenta
 * por cobrar en Cartera.tsx -- mismo patrón de join anidado que ya usa
 * Reportes.tsx (`ventas ( venta_detalle ( servicios ( nombre ), productos
 * ( nombre ) ) )`). */
export interface VentaDetalleConNombre extends VentaDetalleRow {
  nombre: string;
}

/** Cuenta por cobrar (cartera de clientas) con el nombre de la clienta ya
 * resuelto para mostrarlo en la lista -- mismo patrón que
 * CuentaPorPagarConProveedor, del lado de ventas. `venta_fecha`/
 * `venta_total` vienen del join a `ventas` -- pedido explícito del
 * usuario de mostrar el detalle de la cuenta (fecha y total de la venta
 * que la originó) en vez de solo el saldo agregado -- ver Cartera.tsx.
 * `venta_items` es el detalle de servicios/productos de esa misma venta
 * -- segundo pedido del usuario, mostrar QUÉ se vendió, no solo cuánto. */
export interface CuentaPorCobrarConCliente extends CuentaPorCobrarRow {
  cliente_nombre: string;
  venta_fecha: string;
  venta_total: number;
  venta_items: VentaDetalleConNombre[];
}

/** Abono de una clienta (`pagos_clientes`) con el nombre de la forma de
 * pago ya resuelto, para el historial de abonos dentro del detalle de
 * cada cuenta por cobrar en Cartera.tsx. */
export interface PagoClienteConFormaPago extends PagoClienteRow {
  forma_pago_nombre: string;
}

export interface ComisionConProfesional extends ComisionRow {
  profesional_nombre: string;
}

export interface PagoEmpleadoConProfesional extends PagoEmpleadoRow {
  profesional_nombre: string;
}

export const CONCEPTOS_PAGO_EMPLEADO: { value: PagoEmpleadoRow['concepto']; label: string }[] = [
  { value: 'salario', label: 'Salario' },
  { value: 'comision', label: 'Comisión' },
  { value: 'bonificacion', label: 'Bonificación' },
  { value: 'anticipo', label: 'Anticipo' },
  { value: 'descuento', label: 'Descuento' },
  { value: 'otro', label: 'Otro' },
];
